'use client'

import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer'
import { Camera, FileText, Image as ImageIcon, Mic, Plus, Send } from 'lucide-react'
import { useState } from 'react'

interface ActionButtonProps {
	icon: React.ElementType
	label: string
}

const ActionButton = ({ icon: Icon, label }: ActionButtonProps) => (
	<div className="flex flex-col items-center gap-2">
		<div className="relative flex h-[72px] w-[72px] cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.10)] transition-transform active:scale-95">
			<div className="absolute top-0 left-0 flex h-5 w-5 -translate-x-1 -translate-y-1 items-center justify-center rounded-full bg-brand-primary text-white outline outline-2 outline-white">
				<Plus className="h-3.5 w-3.5" strokeWidth={3} />
			</div>
			<Icon className="h-7 w-7 text-gray-700" strokeWidth={1.5} />
		</div>
		<span className="text-sm font-medium text-gray-700">{label}</span>
	</div>
)

interface ChatInputProps {
	onSendMessage: (message: string) => void
	isLoading?: boolean
}

const ChatInput = ({ onSendMessage, isLoading = false }: ChatInputProps) => {
	const [message, setMessage] = useState('')

	const handleSend = () => {
		if (message.trim() && !isLoading) {
			onSendMessage(message)
			setMessage('')
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSend()
		}
	}

	const hasText = message.trim().length > 0

	return (
		<div className="w-full pb-4">
			<Drawer>
				<div className="relative flex w-full items-center rounded-full border border-gray-200 bg-white px-3 py-2 shadow-sm">
					<DrawerTrigger asChild>
						<button
							className="mr-2 flex h-9 w-9 items-center justify-center rounded-full bg-transparent text-brand-primary transition-colors hover:bg-brand-primary/10"
							aria-label="Adjuntar archivo"
						>
							<Plus className="h-6 w-6" strokeWidth={2.5} />
						</button>
					</DrawerTrigger>

					<input
						type="text"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Escribe tu mensaje…"
						className="flex-1 border-none bg-transparent text-[15px] text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
						disabled={isLoading}
					/>

					{hasText ? (
						<button
							onClick={handleSend}
							disabled={isLoading}
							aria-label="Enviar mensaje"
							className="ml-2 flex h-9 w-9 items-center justify-center rounded-full bg-brand-primary text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
						>
							<Send className="h-4 w-4 -translate-x-0.5 rotate-45 transform" strokeWidth={2} />
						</button>
					) : (
						<button
							className="ml-2 flex h-9 w-9 items-center justify-center rounded-full bg-transparent text-brand-primary transition-colors hover:bg-brand-primary/10"
							aria-label="Mensaje de voz"
						>
							<Mic className="h-5 w-5" strokeWidth={2.5} />
						</button>
					)}
				</div>

				<DrawerContent className="rounded-t-[24px] border-none bg-white">
					<div className="mx-auto w-full max-w-sm">
						<div className="p-6 pb-12">
							<DrawerTitle className="sr-only">Adjuntar archivo</DrawerTitle>
							<DrawerDescription className="sr-only">Selecciona el tipo de archivo</DrawerDescription>
							<div className="flex items-center justify-around">
								<ActionButton icon={FileText} label="Documento" />
								<ActionButton icon={ImageIcon} label="Fotos" />
								<ActionButton icon={Camera} label="Cámara" />
							</div>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	)
}

export default ChatInput
